<template>
  <section class="hero">
    <div class="hero__carousel">
      <!-- 轮播图 -->
      <div
          v-for="(slide, index) in slides"
          :key="index"
          class="hero__slide"
          :class="{ 'hero__slide--active': index === currentIndex }"
      >
        <img :src="slide.image" :alt="slide.title" />
        <!-- 文字浮层（可选，如果图片自带文案可以删掉这一层） -->
        <div v-if="slide.title" class="hero__caption">
          <h2 class="hero__caption-title">{{ slide.title }}</h2>
          <p v-if="slide.subtitle" class="hero__caption-subtitle">{{ slide.subtitle }}</p>
          <router-link v-if="slide.link" :to="slide.link" class="btn btn--primary btn--lg">
            {{ slide.linkText || '了解更多' }}
          </router-link>
        </div>
      </div>

      <!-- 左右箭头 -->
      <button class="hero__arrow hero__arrow--prev" @click="prev" aria-label="上一张">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="hero__arrow hero__arrow--next" @click="next" aria-label="下一张">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- 底部指示点 -->
      <div class="hero__dots">
        <button
            v-for="(slide, index) in slides"
            :key="'dot-' + index"
            class="hero__dot"
            :class="{ 'hero__dot--active': index === currentIndex }"
            @click="goTo(index)"
            :aria-label="'第' + (index + 1) + '张'"
        ></button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

// ===== 轮播数据 =====
// 你后面往数组里加图即可，格式：{ image: '图片地址', title: '标题', subtitle: '副标题', link: '跳转路径', linkText: '按钮文字' }
// title / subtitle / link 都是可选的，图片上已经带文案的话可以留空
const slides = ref([
  {
    image: 'https://ltfxpz.oss-cn-shanghai.aliyuncs.com/other/%E4%BC%81%E4%B8%9A%E5%BE%AE%E4%BF%A1%E6%88%AA%E5%9B%BE_17923753569209.png',
    title: '',
    subtitle: '',
    link: '',
    linkText: ''
  }
])

const currentIndex = ref(0)
let timer = null

const next = () => {
  currentIndex.value = (currentIndex.value + 1) % slides.value.length
}
const prev = () => {
  currentIndex.value = (currentIndex.value - 1 + slides.value.length) % slides.value.length
}
const goTo = (index) => {
  currentIndex.value = index
  restartTimer()
}

const startTimer = () => {
  if (slides.value.length <= 1) return
  timer = setInterval(next, 5000) // 5 秒切换
}
const stopTimer = () => {
  if (timer) clearInterval(timer)
  timer = null
}
const restartTimer = () => {
  stopTimer()
  startTimer()
}

onMounted(() => {
  startTimer()
})
onUnmounted(() => {
  stopTimer()
})
</script>

<style scoped>
/* ===== 外层 ===== */
.hero {
  width: 100%;
  padding-top: 96px;     /* 给固定导航栏让位 */
  background: #fff;
}

/* ===== 轮播容器：固定高度，不撑满屏幕 ===== */
.hero__carousel {
  position: relative;
  width: 100%;
  height: 560px;         /* 关键：固定高度，下方内容会露出来 */
  overflow: hidden;
  background: #e8f2fb;
}

/* 大屏稍高一点 */
@media (min-width: 1440px) {
  .hero__carousel {
    height: 640px;
  }
}

/* 移动端更矮 */
@media (max-width: 768px) {
  .hero__carousel {
    height: 320px;
  }
  .hero {
    padding-top: 72px;
  }
}

/* ===== 单张 slide ===== */
.hero__slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.7s ease;
  pointer-events: none;
}
.hero__slide--active {
  opacity: 1;
  pointer-events: auto;
}
.hero__slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

/* ===== 文字浮层（图片自带文案可删） ===== */
.hero__caption {
  position: absolute;
  left: 8%;
  top: 50%;
  transform: translateY(-50%);
  max-width: 520px;
  color: #fff;
}
.hero__caption-title {
  font-size: clamp(28px, 3.4vw, 44px);
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 16px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}
.hero__caption-subtitle {
  font-size: clamp(14px, 1.4vw, 18px);
  line-height: 1.7;
  margin-bottom: 24px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* ===== 左右箭头 ===== */
.hero__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.85);
  color: #1f2d3d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.25s, transform 0.25s, opacity 0.25s;
  opacity: 0;
  z-index: 5;
}
.hero__carousel:hover .hero__arrow {
  opacity: 1;
}
.hero__arrow:hover {
  background: #fff;
  transform: translateY(-50%) scale(1.08);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}
.hero__arrow--prev { left: 24px; }
.hero__arrow--next { right: 24px; }

/* ===== 底部指示点 ===== */
.hero__dots {
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 5;
}
.hero__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  transition: width 0.3s, background 0.3s;
  padding: 0;
}
.hero__dot--active {
  width: 28px;
  border-radius: 6px;
  background: #fff;
}

/* ===== 移动端箭头缩小 ===== */
@media (max-width: 768px) {
  .hero__arrow {
    width: 36px;
    height: 36px;
  }
  .hero__arrow--prev { left: 12px; }
  .hero__arrow--next { right: 12px; }
  .hero__dots { bottom: 14px; }
}
</style>
